// src/store/stream/shareAttach.ts
/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.92, "tu3": 5, "notes": "The share stage of sending a message: resolves an inline share draft or a URL-only message into a shared_items row + message preview before the message is inserted. Calls create-shared-item / extract-url-metadata", "tuVersion": "2.5"}
 * .help "Before a message is saved, works out whether it carries a share (a link you dropped in, or a message that is just a URL), creates the Share Finder item when asked to, and returns the preview the message should display."
 */
import type { Comment, InlineShareDraft, User } from '../../types';
import { supabase } from '../../lib/supabase';
import { detectMediaTypeFromUrl } from '../../utils/inlineMediaUtils';
import { updateSharedItemById } from '../../lib/supabase/queries/share-queries';
import { inlineShareDetails, shareRecipientType, type SendStreamType } from './sendPlan';

export interface ShareAttachInput {
  content: string;
  streamType: SendStreamType;
  recipientId: string;
  sender: Pick<User, 'id' | 'companyId'>;
  inlineShare?: InlineShareDraft;
  /** Caller already attached a shared item; nothing to resolve. */
  sharedItemId?: string;
  skipAutoUrlExtraction?: boolean;
}

export interface ShareAttachResult {
  sharedItemId: string | null;
  sharedItemDetails: Comment['sharedItemDetails'] | null;
}

const NONE: ShareAttachResult = { sharedItemId: null, sharedItemDetails: null };

/**
 * Resolve the share a message carries, if any. Never throws: a failed share must not block the
 * message, which then sends as plain text.
 */
export async function resolveMessageShare(input: ShareAttachInput): Promise<ShareAttachResult> {
  const { inlineShare } = input;

  if (inlineShare) {
    let draft = inlineShare;
    // Embed may have cached full metadata on the store while the draft omitted it.
    if (inlineShare.sharedItemId && !inlineShare.urlMetadata) {
      try {
        const { default: useSharedItemStore } = await import('../sharedItemStore');
        const cached = useSharedItemStore.getState().sharedItems.find((i) => i.id === inlineShare.sharedItemId);
        if (cached?.urlMetadata || cached?.driveFileMetadata) {
          draft = {
            ...inlineShare,
            urlMetadata: cached.urlMetadata ?? inlineShare.urlMetadata,
            driveFileMetadata: cached.driveFileMetadata ?? inlineShare.driveFileMetadata,
            urlMetadataId: cached.url_metadata_id ?? inlineShare.urlMetadataId,
            driveFileMetadataId: cached.drive_file_metadata_id ?? inlineShare.driveFileMetadataId,
          };
        }
      } catch {
        // Store hydrate is best-effort; message still sends.
      }
    }
    const details = inlineShareDetails(draft, detectMediaTypeFromUrl);

    // Embed already created the row (createEmbeddedShare / FromFile).
    if (draft.sharedItemId) {
      if (draft.saveToShareFinder) {
        try {
          await updateSharedItemById(draft.sharedItemId, {
            save_to_share_finder: true,
            title: details.title,
            description: details.description ?? null,
            tags: details.tags ?? [],
          });
        } catch (e) {
          console.warn('[shareAttach] save_to_share_finder update failed:', e);
        }
      }
      return { sharedItemId: draft.sharedItemId, sharedItemDetails: details };
    }

    if (!inlineShare.saveToShareFinder) return { sharedItemId: null, sharedItemDetails: details };

    const sharedItemId = await createSharedItem(input, {
      title: details.title,
      description: details.description ?? null,
      url: details.url,
      type: details.type,
      tags: details.tags ?? [],
      url_metadata_id: inlineShare.urlMetadataId ?? null,
      drive_file_metadata_id: inlineShare.driveFileMetadataId ?? null,
      // Must pass through — edge fn defaults to false and Share Finder would never see it.
      save_to_share_finder: true,
    });
    // Inline share failed to persist: still show the preview; there is just no Share Finder row.
    return { sharedItemId, sharedItemDetails: details };
  }

  // Oct 2026 muted paste: no auto-preview on send. Rich share only when the user
  // clicked Embed (inlineShare draft above). Lone URLs stay raw links.
  return NONE;
}

interface SharedItemFields {
  title: string;
  description: string | null;
  url: string;
  type: string;
  tags: string[];
  url_metadata_id: string | null;
  drive_file_metadata_id: string | null;
  save_to_share_finder?: boolean;
}

async function createSharedItem(input: ShareAttachInput, fields: SharedItemFields): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke('create-shared-item', {
      body: {
        shared_item_data: {
          ...fields,
          company_id: input.sender.companyId,
          created_by_id: input.sender.id,
          team_id: null,
          card_id: null,
          requirement_id: null,
        },
        recipients: [{ recipient_type: shareRecipientType(input.streamType), recipient_id: input.recipientId }],
      },
    });
    if (error || !data?.shared_item_id) {
      console.warn('[shareAttach] create-shared-item failed:', error?.message);
      return null;
    }
    return data.shared_item_id as string;
  } catch (e) {
    console.warn('[shareAttach] create-shared-item threw:', e);
    return null;
  }
}
